import React, { useState } from 'react';
import { Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import AdministrationDashboard from './AdministrationDashboard'; 
import Swal from 'sweetalert2';
import { Building } from 'lucide-react';
import AsyncStorage from '@react-native-async-storage/async-storage'; // Ensure you have this package installed
import { API_URL, UPLOADS_API_URL } from './config';

const AdminCreateCompany = () => {
  const [companyData, setCompanyData] = useState({
    company_id: null, // Set company_id to null
    name: "",
    address: "",
    registration_number: "",
    industry: "",
    company_size: "",
    trading_name: "",
    phone_number1: "",
    phone_number2: "",
    email1: "",
    email2: "",
    mode_transaction_size: "",
    number_of_transactions_per_day: 0,
    bank_account_id1: "",
    bank_account_id2: "",
    bp_number: "",
    vat_number: "",
    base_currency: "USD",
    vat_tax_rate: 0,
    discount_rate: 0,
    physical_address: "",
    portrait_logo: null,
    salutation: "",
    letterhead: null
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setCompanyData({ ...companyData, [name]: files ? files[0] : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const companyId = localStorage.getItem("async_client_profile_id");
    const branchId = localStorage.getItem("branch_id");
    const syncid = "abcd123";

    if (!companyData.name || !companyData.registration_number || !companyData.industry) {
      Swal.fire({
        title: 'Error',
        text: 'Company name, registration number, and industry are required.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setIsLoading(false);
      return;
    }

    try {
      const uploadedImagePaths = await uploadImages(companyId, branchId, syncid);
      const finalCompanyData = {
        ...companyData,
        company_setup_id: undefined, // Exclude this to let the server handle it
        portrait_logo: uploadedImagePaths[0] || null,
        interest_rate: null
      };

      const companyResponse = await fetch(`${API_URL}/companysetup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalCompanyData),
      });

      const responseText = await companyResponse.text();

      if (!companyResponse.ok) {
        throw new Error(responseText);
      }

      const responseData = JSON.parse(responseText);

      // Store the newly created company ID in local storage
      await AsyncStorage.setItem('company_id', responseData.company_id);
      await AsyncStorage.setItem('company_name', finalCompanyData.name);

      // Log the new company ID
      console.log('New Company ID:', responseData.company_id);

      Swal.fire({
        title: 'Success!',
        text: responseData.message || 'Company setup completed successfully!',
        icon: 'success',
        confirmButtonText: 'OK'
      });

    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImages = async (companyId, branchId, syncid) => {
    const fileInputs = [
      { name: 'portrait_logo', label: 'Logo' },
      { name: 'letterhead', label: 'Letterhead' }
    ];

    const imageUploadPromises = fileInputs.map(async (fileInput) => {
      const file = companyData[fileInput.name];
      if (file) {
        const formDataToUpload = new FormData();
        formDataToUpload.append('company_id', companyId);
        formDataToUpload.append('branch_id', branchId);
        formDataToUpload.append('syncid', syncid);
        formDataToUpload.append('image', file);

        try {
          const response = await fetch(`${UPLOADS_API_URL}/uploads`, {
            method: "POST",
            body: formDataToUpload,
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to upload ${fileInput.label}: ${errorData.message || response.statusText}`);
          }

          const responseData = await response.json();
          return responseData.path;
        } catch (error) {
          console.error('Upload error:', error);
          throw new Error(`Error uploading ${fileInput.label}: ${error.message}`);
        }
      }
      return null; // No file to upload
    });

    return Promise.all(imageUploadPromises);
  };

  const industryOptions = [
    "Technology",
    "Manufacturing",
    "Retail",
    "Healthcare",
    "Finance",
    "Education",
    "Energy",
    "Transportation",
    "Telecommunications",
    "Construction",
    "Hospitality",
    "Real Estate",
    "Media",
    "Pharmaceuticals",
    "Agriculture",
    "Entertainment",
    "Automotive",
    "Aerospace",
    "Insurance",
    "Consulting",
    "Non-Profit"
];

  const companySizeOptions = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "500+ employees"
  ];

  return (
    <AdministrationDashboard>
      <div className="create-company-container">
        <Card className="shadow-sm">
          <Card.Header 
            className="bg-purple text-white py-3"
            style={{ backgroundColor: '#B03F82' }}
          >
            <div className="d-flex align-items-center">
              <Building size={24} className="me-2" />
              <h5 className="mb-0">Create New Company</h5>
            </div>
          </Card.Header>

          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <section className="mb-4">
                <h6 className="mb-3 text-purple fw-bold" style={{ textAlign: "start" }}>Basic Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Company Name*</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={companyData.name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Trading Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="trading_name"
                        value={companyData.trading_name}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Registration Number*</Form.Label>
                      <Form.Control
                        type="text"
                        name="registration_number"
                        value={companyData.registration_number}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Industry*</Form.Label>
                      <Form.Select
                        name="industry"
                        value={companyData.industry}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Industry</option>
                        {industryOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Contact Information */}
              <section className="mb-4" style={{ textAlign: "start" }}>
                <h6 className="mb-3 text-purple fw-bold">Contact Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone_number1"
                        value={companyData.phone_number1}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Secondary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone_number2"
                        value={companyData.phone_number2}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Primary Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email1"
                        value={companyData.email1}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Secondary Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email2"
                        value={companyData.email2}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Business Details */}
              <section className="mb-4" style={{ textAlign: "start" }}>
                <h6 className="mb-3 text-purple fw-bold">Business Details</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Size</Form.Label>
                      <Form.Select
                        name="company_size"
                        value={companyData.company_size}
                        onChange={handleChange}
                      >
                        <option value="">Select Company Size</option>
                        {companySizeOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>VAT Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="vat_number"
                        value={companyData.vat_number}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Base Currency</Form.Label>
                      <Form.Select
                        name="base_currency"
                        value={companyData.base_currency}
                        onChange={handleChange}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>VAT Tax Rate (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="vat_tax_rate"
                        value={companyData.vat_tax_rate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Address */}
              <section className="mb-4" style={{ textAlign: "start" }}>
                <h6 className="mb-3 text-purple fw-bold">Address Information</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Physical Address</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="physical_address"
                        value={companyData.physical_address}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Company Assets */}
              <section className="mb-4" style={{ textAlign: "start" }}>
                <h6 className="mb-3 text-purple fw-bold">Company Assets</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Company Logo</Form.Label>
                      <Form.Control
                        type="file"
                        name="portrait_logo"
                        onChange={handleChange}
                        accept="image/*"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" style={{ textAlign: "start" }}>
                      <Form.Label>Company Letterhead</Form.Label>
                      <Form.Control
                        type="file"
                        name="letterhead"
                        onChange={handleChange}
                        accept="image/*"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <div className="d-flex justify-content-end mt-4">
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  style={{ backgroundColor: '#B03F82', borderColor: '#B03F82' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    'Create Company'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <style jsx>{`
        .text-purple {
          color: #B03F82;
        }
        .create-company-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .form-control:focus, .form-select:focus {
          border-color: #B03F82;
          box-shadow: 0 0 0 0.25rem rgba(111, 66, 193, 0.25);
        }
      `}</style>
    </AdministrationDashboard>
  );
};

export default AdminCreateCompany;